import { chunk, compact, filter, flatMap, map, omit, uniqBy } from 'es-toolkit/compat';
import pLimit from 'p-limit';

import { getPreSignedPresentationInfo } from 'api/experimentResults';
import { getProcedures } from 'api/procedures';
import { getSlidesDataForArtifactsResultsPage } from 'api/study';
import {
  getParquetArtifactEntriesFromExperimentResult,
  ParquetArtifactEntry,
} from 'components/Procedure/useSlideChannelsAndResults/parquetArtifactFiles';
import { ExperimentResult, FlowClassName, ResultType } from 'interfaces/experimentResults';
import { AttributesTable, slideResultToAttributesTable } from 'interfaces/inferenceArtifacts';
import { ProcedureResponse } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';
import { casesOptions, casesSchema, paginationAndSortingParams } from 'utils/useCasesParams/constants';
import { CasesParams } from 'utils/useCasesParams/types';

const PROCEDURE_SLIDE_ID_BATCH_SIZE = 25;
export const CELL_TABLE_DISCOVERY_CONCURRENCY = 5;

export interface CellTableArtifactFile extends ParquetArtifactEntry {
  caseId: string;
  slideId: string;
  resultType?: string;
}

export interface CellTableArtifactDiscoveryDependencies {
  getSlidesDataPage: typeof getSlidesDataForArtifactsResultsPage;
  getProcedureResults: typeof getProcedures;
  getPresentationInfo: typeof getPreSignedPresentationInfo;
  limit: pLimit.Limit;
}

const createDefaultDependencies = (): CellTableArtifactDiscoveryDependencies => ({
  getSlidesDataPage: getSlidesDataForArtifactsResultsPage,
  getProcedureResults: getProcedures,
  getPresentationInfo: getPreSignedPresentationInfo,
  limit: pLimit(CELL_TABLE_DISCOVERY_CONCURRENCY),
});

export interface CellTableArtifactDiscoveryProgress {
  completedPages: number;
  inspectedSlides: number;
  discoveredFiles: number;
}

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Cell-table artifact discovery was cancelled', 'AbortError');
  }
};

const encodeProcedureResultParams = (casesParams: CasesParams, slideIdsToInclude: string[]) =>
  encodeQueryParamsUsingSchema(
    {
      ...omit(casesParams, paginationAndSortingParams),
      caseIdsToInclude: undefined,
      caseIdsToExclude: undefined,
      slideIdsToInclude,
      slideIdsToExclude: undefined,
      skipPagination: true,
      experimentResultsSelection: ExperimentResultsSelection.WithoutFeatures,
    },
    casesSchema,
    casesOptions
  );

export interface CellTableArtifactDiscoveryOptions {
  // Called once with how many files shared a URL with another file and were listed only once.
  onDuplicatesCollapsed?: (count: number) => void;
}

const getEligibleExperimentResults = (slide: Slide): ExperimentResult[] =>
  filter(
    slide.experimentResults || [],
    (experimentResult) =>
      experimentResult.flowClassName === FlowClassName.CalculateFeatures ||
      (Boolean(experimentResult.resultType) &&
        slideResultToAttributesTable[experimentResult.resultType] === AttributesTable.CellTable)
  );

export const discoverCellTableArtifactFiles = async (
  casesParams: CasesParams,
  signal: AbortSignal,
  dependencies: CellTableArtifactDiscoveryDependencies = createDefaultDependencies(),
  onProgress?: (progress: CellTableArtifactDiscoveryProgress) => void,
  options: CellTableArtifactDiscoveryOptions = {}
): Promise<CellTableArtifactFile[]> => {
  const artifacts: CellTableArtifactFile[] = [];
  let cursor: string | undefined;
  let completedPages = 0;
  let inspectedSlides = 0;

  do {
    throwIfAborted(signal);
    const page = await dependencies.getSlidesDataPage(omit(casesParams, paginationAndSortingParams), signal, cursor);
    throwIfAborted(signal);

    const selectedSlideIds = map(page.items, 'slideId');
    const procedureResponses = await Promise.all(
      map(chunk(selectedSlideIds, PROCEDURE_SLIDE_ID_BATCH_SIZE), (slideIds) =>
        dependencies.limit(async () => {
          throwIfAborted(signal);
          const response = await dependencies.getProcedureResults(
            encodeProcedureResultParams(casesParams, slideIds),
            signal
          );
          throwIfAborted(signal);
          return response;
        })
      )
    );
    throwIfAborted(signal);

    const selectedSlideIdSet = new Set(selectedSlideIds);
    const procedureSlides = flatMap(procedureResponses, (response: ProcedureResponse) =>
      flatMap(response.procedures, (procedure) =>
        map(
          filter(procedure.slides || [], (slide) => selectedSlideIdSet.has(slide.id)),
          (slide) => ({ procedure, slide })
        )
      )
    );

    const artifactGroups = await Promise.all(
      flatMap(procedureSlides, ({ procedure, slide }) =>
        map(getEligibleExperimentResults(slide), (experimentResult) =>
          dependencies.limit(async () => {
            throwIfAborted(signal);
            const studyId = casesParams.studyId || casesParams.filters?.studyId;
            const presignedPresentationInfo = await dependencies.getPresentationInfo(
              studyId,
              experimentResult.experimentResultId,
              signal
            );
            throwIfAborted(signal);

            const title = `${procedure.label || procedure.id} / ${slide.originalFileName || slide.id}`;
            const presentedResult = {
              ...experimentResult,
              presentationInfo: presignedPresentationInfo?.[experimentResult.experimentResultId],
              resultType: experimentResult.resultType ?? ResultType.PipsCellsTable,
            };
            return map(
              getParquetArtifactEntriesFromExperimentResult(presentedResult, title, AttributesTable.CellTable),
              (artifact) => ({
                ...artifact,
                caseId: `${procedure.id}`,
                slideId: slide.id,
                resultType: experimentResult.resultType,
              })
            );
          })
        )
      )
    );
    throwIfAborted(signal);

    artifacts.push(...compact(flatMap(artifactGroups)));
    completedPages += 1;
    inspectedSlides += selectedSlideIds.length;
    onProgress?.({
      completedPages,
      inspectedSlides,
      discoveredFiles: uniqBy(artifacts, 'urlForGetRequest').length,
    });
    cursor = page.nextCursor;
  } while (cursor);

  const uniqueArtifacts = uniqBy(artifacts, 'urlForGetRequest');
  options.onDuplicatesCollapsed?.(artifacts.length - uniqueArtifacts.length);

  return map(uniqueArtifacts, (artifact, artifactIndex) => ({
    ...artifact,
    id: `${artifact.id}:${artifact.caseId}:${artifact.slideId}:${artifactIndex}`,
  }));
};
